import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { orgService, vehicleService, geofenceService } from "@/services/api";
import { MapCanvas } from "@/components/common/MapCanvas";
import { StatusBadge } from "@/components/common/StatusBadge";
import { StatItem } from "@/components/common/Telemetry";
import { SkeletonMap } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { ParkingPanel } from "@/components/parking/ParkingPanel";
import { ArrowLeft, MapPin, Car } from "lucide-react";

export default function FacilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: f, loading } = useFetch(() => orgService.facility(id), [id]);
  // Real vehicles have a facility_id (which yard they're homed to), not the
  // mock's hand-picked "currently inside the fence" x/y groupings — so this
  // is "vehicles based here", split by parked (likely on-site) vs. not,
  // rather than a geofence-verified inside/outside reading.
  const { data: facilityVehicles } = useFetch(
    () => (f ? vehicleService.list({ facilityId: f.id }) : Promise.resolve([])),
    [f?.id]
  );
  const { data: allGeofences } = useFetch(() => geofenceService.list(), []);

  if (loading) return <div className="p-6"><SkeletonMap className="h-[420px]" /></div>;
  if (!f) return <div className="p-8"><EmptyState title="Facility not found" action="Back to Facilities" onAction={() => navigate("/facilities")} /></div>;

  const vehiclesHere = facilityVehicles || [];
  const parked = vehiclesHere.filter((v) => v.status === "parked");
  const away = vehiclesHere.filter((v) => v.status !== "parked");
  const facilityGeofences = (allGeofences || []).filter((g) => g.facilityId === f.id);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <button onClick={() => navigate("/facilities")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Facilities</button>

      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{f.name}</h1>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {f.address}</p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatItem label="Total Vehicles" value={f.vehicles} />
        <StatItem label="Active" value={f.activeVehicles} />
        <StatItem label="Alerts" value={f.alerts} />
        <StatItem label="Geofences" value={f.geofences} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="h-[480px]">
          <MapCanvas vehicles={vehiclesHere} facilities={[f]} geofences={facilityGeofences} className="h-full" testId="facility-map" />
        </div>
        <div className="space-y-4">
          <Card className="border-border/70 bg-card/60 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Parked here <span className="ml-auto text-muted-foreground">{parked.length}</span></div>
            <div className="space-y-2">
              {parked.length === 0 ? <p className="text-sm text-muted-foreground">No vehicles parked here.</p> : parked.map((v) => <VRow key={v.id} v={v} onClick={() => navigate(`/vehicles/${v.id}`)} />)}
            </div>
          </Card>
          <Card className="border-border/70 bg-card/60 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium"><span className="h-2 w-2 rounded-full bg-sky-400" /> Based here, currently out <span className="ml-auto text-muted-foreground">{away.length}</span></div>
            <div className="space-y-2">
              {away.length === 0 ? <p className="text-sm text-muted-foreground">No vehicles out.</p> : away.map((v) => <VRow key={v.id} v={v} onClick={() => navigate(`/vehicles/${v.id}`)} />)}
            </div>
          </Card>
        </div>
      </div>

      <ParkingPanel facility={f} />
    </div>
  );
}

function VRow({ v, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-secondary/20 p-2.5 text-left transition hover:border-border hover:bg-secondary/40">
      <Car className="h-4 w-4 text-muted-foreground" />
      <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{v.name}</div><div className="truncate text-xs text-muted-foreground">{v.fleetTag}</div></div>
      <StatusBadge status={v.status} size="sm" />
    </button>
  );
}
